/** Local, numeric diagnostics. This module never emits request URLs or credentials. */
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const meta =
  typeof document === "undefined"
    ? null
    : document.querySelector<HTMLMetaElement>('meta[name="ato-startup-trace"]')
        ?.content;
export const navigationTraceId =
  meta && UUID.test(meta) ? meta : crypto.randomUUID();

export function traceStartupUrl(input: string, attempt: number) {
  const url = new URL(
    input,
    typeof window === "undefined" ? undefined : window.location.href,
  );
  url.searchParams.set("__ato_trace", navigationTraceId);
  url.searchParams.set("__ato_attempt", String(attempt));
  return url.toString();
}

export function httpEvidence(entry: PerformanceResourceTiming) {
  const fields = [
    "startTime",
    "workerStart",
    "fetchStart",
    "redirectStart",
    "redirectEnd",
    "domainLookupStart",
    "domainLookupEnd",
    "connectStart",
    "connectEnd",
    "secureConnectionStart",
    "requestStart",
    "responseStart",
    "responseEnd",
    "duration",
    "transferSize",
    "encodedBodySize",
    "decodedBodySize",
  ] as const;
  const times: Record<string, number> = {};
  for (const key of fields)
    if (Number.isFinite(entry[key]) && entry[key] >= 0) times[key] = entry[key];
  const server = (entry.serverTiming ?? [])
    .filter((item) => /^[a-zA-Z0-9_-]{1,64}$/.test(item.name))
    .slice(0, 40)
    .map((item) => ({
      name: item.name,
      duration: item.duration,
      ...(/^ato_(trace|request|queries|db_calls)$/.test(item.name) &&
      (UUID.test(item.description) || /^\d{1,8}$/.test(item.description))
        ? { description: item.description }
        : {}),
    }));
  return {
    times,
    server,
    protocol: entry.nextHopProtocol,
    service_worker: entry.workerStart > 0,
    status: "responseStatus" in entry ? entry.responseStatus : undefined,
  };
}

export function installStartupHttpTiming() {
  if (typeof PerformanceObserver === "undefined") return;
  const report = (entry: PerformanceEntry) => {
    if (entry.entryType === "navigation") {
      console.debug(
        "[ato:http-timing]",
        JSON.stringify({
          trace_id: navigationTraceId,
          attempt: 1,
          kind: "parent",
          ...httpEvidence(entry as PerformanceNavigationTiming),
        }),
      );
    } else {
      const url = new URL(entry.name);
      if (
        url.searchParams.get("__ato_trace") !== navigationTraceId ||
        !/^\/v1\/discover\/(?:entry|apps\/[^/]+)\/start$/.test(url.pathname)
      )
        return;
      console.debug(
        "[ato:http-timing]",
        JSON.stringify({
          trace_id: navigationTraceId,
          attempt: Number(url.searchParams.get("__ato_attempt")),
          kind: "startup",
          ...httpEvidence(entry as PerformanceResourceTiming),
        }),
      );
    }
  };
  const observer = new PerformanceObserver((list) =>
    list.getEntries().forEach(report),
  );
  observer.observe({ type: "resource", buffered: true });
  const navigation = new PerformanceObserver((list) =>
    list.getEntries().forEach(report),
  );
  navigation.observe({ type: "navigation", buffered: true });
}
